import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { tap } from 'rxjs';

import { AccountSelectors, CurrentUserSelectors } from '@recruitee/account-data';
import { filterBoolean } from '@recruitee/common';
import { FeatureDiscoveryActions } from '@recruitee/feature-discovery-data';
import { JobBoardCodename } from '@recruitee/job-boards-types';
import { JobItemsSelectors } from '@recruitee/offers-data';
import {
  FirstJobPublishedDialogComponent,
  JobPublishedDialogComponent,
} from '@recruitee/offers-smart-ui';
import { OfferItem, OfferStatus } from '@recruitee/offers-types';
import { DialogService, DialogWidth } from '@recruitee/user-interface';

@Injectable({
  providedIn: 'root',
})
export class JobPublishedDialogFacade {
  private autoPostings: Array<JobBoardCodename.GoogleForJobs | JobBoardCodename.Indeed> = [];

  constructor(
    private store: Store,
    private dialogService: DialogService,
    private destroyRef: DestroyRef,
    private router: Router,
  ) {
    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(filterBoolean, takeUntilDestroyed(this.destroyRef))
      .subscribe(company => {
        this.autoPostings = company.autoPostings;
      });
  }

  public open(jobId: number, isJobEditorContext = false): void {
    const offer = this.store.selectSignal(JobItemsSelectors.byId(jobId))();
    const currentAdmin = this.store.selectSignal(CurrentUserSelectors.data)();
    if (!offer) return;

    const isFirstJobPublished = currentAdmin?.featureDiscovery.firstJobPublishedUsed;

    if (!isFirstJobPublished) {
      this.openFirstJobPublishedDialog(offer);
    } else {
      this.openJobPublishedDialog(offer, isJobEditorContext);
    }
  }

  private openJobPublishedDialog(offer: OfferItem, isJobEditorContext: boolean): void {
    const status = <OfferStatus.Internal | OfferStatus.Published>offer.status;
    const title = offer.title;
    const department = offer.department || null;
    const locationIds = offer.locationIds || [];
    const workModel = {
      onSite: offer.onSite,
      remote: offer.remote,
      hybrid: offer.hybrid,
    };
    const priority = offer.priority || null;

    this.dialogService
      .openCustom(
        JobPublishedDialogComponent,
        { width: DialogWidth.Small },
        {
          status,
          title,
          department,
          locationIds,
          workModel,
          priority,
          autoPostings: this.autoPostings,
          isJobEditorContext,
        },
      )
      .afterClosed.pipe(
        filterBoolean,
        tap(() => this.redirect(status, offer.slug)),
      )
      .subscribe();
  }

  private openFirstJobPublishedDialog(offer: OfferItem): void {
    const status = <OfferStatus.Internal | OfferStatus.Published>offer.status;

    this.dialogService
      .openCustom(FirstJobPublishedDialogComponent, { width: DialogWidth.Small }, { status })
      .afterClosed.pipe(
        tap(() => {
          this.store.dispatch(
            FeatureDiscoveryActions.update({ payload: { firstJobPublishedUsed: true } }),
          );
        }),
        filterBoolean,
        tap(() => this.redirect(status, offer.slug)),
      )
      .subscribe();
  }

  private redirect(jobStatus: OfferStatus, jobSlug: string): void {
    this.router.navigate([{ outlets: { 'job-editor': null } }]).then(() => {
      if (jobStatus === OfferStatus.Published) {
        this.router.navigate(['offers', jobSlug, 'promote']);
      } else {
        this.router.navigate(['offers', jobSlug, 'pipeline']);
      }
    });
  }
}
